// Iter-276.E (audit Top-20 #14) — Shared comparison-table skeleton.
//
// Audit finding: 27 comparison surfaces each shipped their own
// skeleton (or none at all), producing inconsistent loading-state
// visual language across the site. The PartnerRateTable already
// shipped the most polished skeleton (staggered animation-delay,
// desktop + mobile variants); this module lifts that out so every
// other surface can drop in a consistent placeholder.
//
// Public API:
//
//   <TableSkeleton
//     rows={5}          // desktop row count
//     mobileRows={3}    // mobile card count
//     columns={6}       // desktop cell columns (4-7 typical)
//     variant="rate"    // "rate" | "compact" — controls layout density
//     testid="lender-rate-table-skeleton"   // optional override
//   />
//
// `variant="rate"` matches the iter-184.1 PartnerRateTable shape with
// logo + name + 5 numeric cells + an Apply pill on the right; mobile
// is a stacked card with a 2-col grid + a CTA pill at the bottom.
// `variant="compact"` is for narrower surfaces (CompareDrawer,
// CompareIndex) — single column, smaller pills, no Apply CTA.
//
// Both variants share the iter-156-grade staggered animationDelay
// pattern so the skeleton has a perceived sequential reveal that
// matches the data-loading mental model.

import React from "react";

function CellPulse({ widthClass = "w-20", heightClass = "h-3", delay = 0 }) {
  return (
    <div
      className={`bg-slate-200 animate-pulse ${heightClass} ${widthClass}`}
      style={{ animationDelay: `${delay}ms` }}
    />
  );
}

export default function TableSkeleton({
  rows = 5,
  mobileRows = 3,
  columns = 6,
  variant = "rate",
  testid = "table-skeleton",
}) {
  const isRate = variant === "rate";
  return (
    <div data-testid={testid} aria-hidden="true">
      {/* ─── Desktop skeleton ───────────────────────────────── */}
      <div className="hidden md:block border border-slate-200 bg-white">
        {/* Header row */}
        <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex gap-6">
          <CellPulse widthClass="w-24" />
          {Array.from({ length: Math.max(0, columns - 2) }).map((_, i) => (
            <CellPulse
              key={`hdr-${i}`}
              widthClass={i === 0 ? "w-16 ml-auto" : "w-20"}
            />
          ))}
          <CellPulse widthClass="w-16" />
        </div>
        {/* Body rows */}
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="border-b border-slate-100 px-5 py-5 flex items-center gap-6 last:border-b-0"
          >
            <div className="flex items-center gap-3 min-w-[160px]">
              <div
                className="w-10 h-10 bg-slate-100 animate-pulse"
                style={{ animationDelay: `${i * 80}ms` }}
              />
              <div className="space-y-1.5">
                <CellPulse widthClass="w-28" heightClass="h-3.5" delay={i * 80} />
                <CellPulse widthClass="w-20" heightClass="h-2.5" delay={i * 80 + 60} />
              </div>
            </div>
            {Array.from({ length: Math.max(0, columns - 2) }).map((_, j) => (
              <CellPulse
                key={`cell-${i}-${j}`}
                widthClass={j === 0 ? "w-16 ml-auto" : ["w-14", "w-20", "w-12"][j % 3] || "w-16"}
                heightClass="h-3.5"
                delay={i * 80 + j * 20 + 40}
              />
            ))}
            {isRate && (
              <div
                className="h-9 bg-slate-200 animate-pulse w-24"
                style={{ animationDelay: `${i * 80 + 120}ms` }}
              />
            )}
          </div>
        ))}
      </div>

      {/* ─── Mobile skeleton ─────────────────────────────────── */}
      <div className="md:hidden space-y-3">
        {Array.from({ length: mobileRows }).map((_, i) => (
          <div
            key={i}
            className="border border-slate-200 bg-white p-4 space-y-3"
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 bg-slate-100 animate-pulse shrink-0"
                style={{ animationDelay: `${i * 100}ms` }}
              />
              <div className="flex-1 space-y-1.5">
                <div
                  className="h-4 bg-slate-200 animate-pulse w-2/3"
                  style={{ animationDelay: `${i * 100}ms` }}
                />
                <div
                  className="h-2.5 bg-slate-100 animate-pulse w-1/2"
                  style={{ animationDelay: `${i * 100 + 60}ms` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div
                className="h-10 bg-slate-100 animate-pulse"
                style={{ animationDelay: `${i * 100 + 80}ms` }}
              />
              <div
                className="h-10 bg-slate-100 animate-pulse"
                style={{ animationDelay: `${i * 100 + 100}ms` }}
              />
            </div>
            {isRate && (
              <div
                className="h-10 bg-slate-200 animate-pulse w-full"
                style={{ animationDelay: `${i * 100 + 140}ms` }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
